import { useState, useEffect, useRef } from 'react';
import { isEmpty } from './func';

const formatWithCommas = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const str = String(raw);
  const sign = str.startsWith('-') ? '-' : '';
  const body = sign ? str.slice(1) : str;
  const [intPart = '', decPart] = body.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (body.includes('.')) {
    return `${sign}${grouped}.${decPart ?? ''}`;
  }
  return `${sign}${grouped}`;
};

const sanitize = (input, { decimalPlaces, allowNegative }) => {
  let s = String(input ?? '').replace(/[^\d.\-]/g, '');
  if (!allowNegative) {
    s = s.replace(/-/g, '');
  } else {
    s = s[0] === '-' ? '-' + s.slice(1).replace(/-/g, '') : s.replace(/-/g, '');
  }
  const firstDot = s.indexOf('.');
  if (firstDot >= 0) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    const parts = s.split('.');
    if (parts[1].length > decimalPlaces) {
      parts[1] = parts[1].slice(0, decimalPlaces);
      s = parts.join('.');
    }
  }
  return s;
};

const Component = ({
  className,
  placeholder,
  value,
  onChange,
  onValidate,
  showError,
  required,
  minLength,
  maxLength,
  disabled,
  decimalPlaces = 8,
  min,
  max,
}) => {
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const nextCaretRef = useRef(null);

  const displayValue = formatWithCommas(value ?? '');

  useEffect(() => {
    if (nextCaretRef.current !== null && inputRef.current) {
      const pos = Math.min(nextCaretRef.current, displayValue.length);
      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch (_) {}
      nextCaretRef.current = null;
    }
  });

  const validate = () => {
    if (isEmpty(value)) {
      if (required) {
        return 'Required.';
      }
      return null;
    }
    if (minLength && minLength > 0) {
      if (value.length < minLength) {
        return 'At least ' + minLength + ' chars.';
      }
    }
    if (maxLength && maxLength > 0) {
      if (value.length > maxLength) {
        return 'At most ' + maxLength + ' chars.';
      }
    }

    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      if (min !== undefined && numericValue < min) {
        return `Value should be at least ${min}.`;
      }
      if (max !== undefined && numericValue > max) {
        return `Value should be at most ${max}.`;
      }
    }
    return null;
  };

  useEffect(() => {
    let msg = validate();
    setError(msg);
    if (onValidate) {
      onValidate(msg == null);
    }
  }, [value]);

  const handleChange = (e) => {
    const el = e.target;
    const rawDisplay = el.value;
    const caret = el.selectionStart ?? rawDisplay.length;

    const allowNegative = !(min !== undefined && min >= 0);
    const stripped = sanitize(rawDisplay, { decimalPlaces, allowNegative });

    const digitsBeforeCaret = rawDisplay
      .slice(0, caret)
      .replace(/,/g, '').length;
    const newFormatted = formatWithCommas(stripped);
    let newCaret = 0;
    if (digitsBeforeCaret > 0) {
      let digitsSeen = 0;
      for (let i = 0; i < newFormatted.length; i++) {
        if (newFormatted[i] !== ',') digitsSeen++;
        if (digitsSeen === digitsBeforeCaret) {
          newCaret = i + 1;
          break;
        }
      }
      if (newCaret === 0) newCaret = newFormatted.length;
    }
    nextCaretRef.current = newCaret;

    if (onChange) {
      onChange({ target: { value: stripped } });
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        pattern="[0-9.,-]*"
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        onWheel={(e) => e.target.blur()}
      />
      {error && showError && <span className="invalid">{error}</span>}
    </>
  );
};

export default Component;
